import { useState, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { ArchiveBoxArrowDownIcon } from '@heroicons/react/24/outline'
import TaskItem from './TaskItem'
import { archiveProcessContainer } from '../processTasksSlice'

const STATUS_BADGES = {
  completed: { label: 'Terminé', className: 'badge-success' },
  archived: { label: 'Archivé', className: 'badge-ghost' }
}

/**
 * Card pour afficher un groupe de tâches d'un ProcessContainer
 *
 * @param {Object} group - { codageInterne, processType, tasks, container, taskSource }
 * @param {Function} onComplete - Compléter une task
 * @param {Function} onStart - Démarrer une task
 * @param {Function} onValidate - Valider une task (admin)
 * @param {Function} onReopen - Rouvrir une task (admin)
 */
const ProcessCard = ({ group, onComplete, onStart, onValidate, onReopen }) => {
  const dispatch = useDispatch()
  const { codageInterne, processType, tasks, container } = group

  const [showCancelModal, setShowCancelModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Tri des tâches selon leur statut
  // Les tâches validées ou annulées sont ne sont pas affichées
  const sortedTasks = useMemo(() => {
    // Les tâches validées ou annulées sont triées en dernières
    // const statusOrder = { pending: 0, in_progress: 0, completed: 0, validated: tasks.length, cancelled: tasks.length + 1 }
    // return [...tasks].sort((a, b) => {
    //   const aOrder = statusOrder[a.status] ?? 999
    //   const bOrder = statusOrder[b.status] ?? 999
    //   if (aOrder !== bOrder) return aOrder - bOrder
    //   return (a.position || 0) - (b.position || 0)
    // })
    return [...tasks].filter( t => !["validated", "cancelled"].includes(t.status) )
  }, [tasks])

  const cardTitle = processType || codageInterne

  const statusBadge = STATUS_BADGES[container.status] || null

  const handleCancelProcess = async () => {
    if (!container?.id) return

    setIsLoading(true)
    try {
      await dispatch(archiveProcessContainer(container.id)).unwrap()
      setShowCancelModal(false)
    } catch (error) {
      console.error('Erreur lors de l\'annulation du process:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const containerIsArchived = container?.status === 'archived'
  const containerIsCompleted = container?.status === 'completed'

  return (
    <div className={`rounded-none p-4 pt-0 relative 
      min-w-[325px] max-w-[500px] max-h-80 overflow-y-scroll overscroll-contain 
      ${
      containerIsCompleted ? 'bg-green-50 border border-green-200' :
      containerIsArchived ? 'bg-gray-100 border border-gray-300 opacity-70' :
      'bg-gray-200'
    }`}>

      {/* Header avec titre et boutons */}
      <div className={`flex justify-between items-start pt-4 mb-3 sticky top-0 bg-inherit z-99`}>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-primary">
            {cardTitle}
            { statusBadge && 
              <span className={`badge ${statusBadge.className} badge-sm mx-2`}>{statusBadge.label}</span>
            }
          </h3>
        </div>

        {/* Boutons d'action (uniquement si container non archivé/complété) */}
        {!containerIsArchived && !containerIsCompleted && (
          <div className="flex gap-1 ml-2">
            <button
              onClick={() => setShowCancelModal(true)}
              disabled={isLoading}
              className="btn btn-sm btn-ghost btn-outline hover:bg-error"
              title="Archiver le process"
            >
              <ArchiveBoxArrowDownIcon className="w-4 h-4"/>
            </button>
          </div>
        )}
      </div>

      {/* Liste des tâches */}
      <div className="space-y-3">
        {sortedTasks.map(task => (
          <TaskItem
            key={`process-${task.id}`}
            task={task}
            bienLabel={group.codageInterne}
            onComplete={(_taskId) => onComplete(task)}
            // onStart={onStart ? (_taskId) => onStart(task) : undefined} // cf commentaire TaskItem.jsx
            onValidate={onValidate}
            onReopen={onReopen}
            showColorIndicator={true}
            showUrgencyBadge={false}
            showDetails={false}
          />
        ))}
      </div>

      {/* Modal de confirmation d'annulation */}
      {showCancelModal && (
        <dialog open className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Archiver le process</h3>
            <p className="py-4">
              Êtes-vous sûr de vouloir archiver ce process ?
              <br />
              <span className="text-sm text-gray-500">
                Les tâches non complétées seront annulées.
              </span>
            </p>
            <div className="modal-action">
              <button
                onClick={handleCancelProcess}
                disabled={isLoading}
                className="btn btn-error"
              >
                {isLoading ? (
                  <span className="loading loading-spinner loading-xs" />
                ) : (
                  'Confirmer'
                )}
              </button>
              <button
                onClick={() => setShowCancelModal(false)}
                disabled={isLoading}
                className="btn btn-outline"
              >
                Annuler
              </button>
            </div>
          </div>
          <div className="modal-backdrop bg-black/50" onClick={() => !isLoading && setShowCancelModal(false)} />
        </dialog>
      )}
    </div>
  )
}

export default ProcessCard
